import React from "react";

import { InfoOutlined } from "@mui/icons-material";
import { Box, TextField, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";

import { EntityErrors } from "@/components/alerts/add-v2/model/entityRules";
import { PersonEntity } from "@/components/alerts/add-v2/model/types";

import { ENTITY_FULL_ROW_SX } from "./entityFieldLayout";

/**
 * First name, last name and date of birth — the three fields the whole rewrite exists to capture,
 * because they are what reaches the board card.
 *
 * They are required only on the bulletin types listed in `IDENTITY_REQUIRED_BULLETIN_TYPES`;
 * `identityRequired` carries that decision in rather than re-deriving it here.
 *
 * There is deliberately **no Alias field** — the design puts an alias in the name fields and its
 * context in Additional description.
 *
 * The errors are `validateEntityDraft`'s, handed down through the editor. Note the date has two:
 * it is required only on the identity bulletin types, but a date that was *typed* has to be
 * readable whichever type this is.
 */

interface PersonFieldsProps {
  value: PersonEntity;
  identityRequired: boolean;
  errors: EntityErrors;
  onPatch: (next: Partial<PersonEntity>) => void;
}

export const PersonFields: React.FC<PersonFieldsProps> = ({
  value,
  identityRequired,
  errors,
  onPatch,
}) => {
  const { t } = useTranslation("bulletin/create-v2");

  const message = (code: string | undefined) =>
    code ? t(`validation.entity_${code}`) : undefined;

  return (
    <>
      <TextField
        size="small"
        fullWidth
        required={identityRequired}
        error={!!errors.firstName}
        helperText={message(errors.firstName)}
        label={t("entities.first_name")}
        value={value.firstName}
        onChange={(event) => onPatch({ firstName: event.target.value })}
      />
      <TextField
        size="small"
        fullWidth
        required={identityRequired}
        error={!!errors.lastName}
        helperText={message(errors.lastName)}
        label={t("entities.last_name")}
        value={value.lastName}
        onChange={(event) => onPatch({ lastName: event.target.value })}
      />

      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
        <TextField
          size="small"
          fullWidth
          required={identityRequired}
          label={t("entities.date_of_birth")}
          placeholder={t("entities.date_of_birth_placeholder")}
          value={value.dateOfBirth ?? ""}
          // Free text on purpose: the officer types whatever format they have. What is stored is
          // whatever was typed; `normalizeDateOfBirth` turns it into `YYYY-MM-DD` in the adapter on
          // the way to the payload, which is where the model's ISO-date contract is met.
          error={!!errors.dateOfBirth}
          helperText={
            message(errors.dateOfBirth) ?? t("entities.date_of_birth_hint")
          }
          onChange={(event) => onPatch({ dateOfBirth: event.target.value })}
        />
        <Tooltip title={t("entities.date_of_birth_info")}>
          <InfoOutlined
            color="info"
            fontSize="small"
            sx={{ mt: 1.25, flexShrink: 0 }}
          />
        </Tooltip>
      </Box>

      <TextField
        size="small"
        fullWidth
        multiline
        minRows={3}
        sx={ENTITY_FULL_ROW_SX}
        label={t("entities.additional_description")}
        placeholder={t("entities.additional_description_person_placeholder")}
        value={value.additionalDescription}
        onChange={(event) =>
          onPatch({ additionalDescription: event.target.value })
        }
      />
    </>
  );
};
